import { useApi } from '@shopexperts/shared';
import type { PartnerBadge } from '@shopexperts/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRef } from 'vue';

/**
 * Resolve a partner badge by slug — the value the expert-listing
 * `partner_badge` filter stores. The badges search endpoint matches name OR
 * slug, so querying with the slug and picking the exact match avoids listing
 * the whole catalog.
 */
export function useBadgeBySlug(slug: MaybeRef<string>) {
  const api = useApi();
  const slugValue = computed(() => toValue(slug).trim());

  const { data, isLoading } = useQuery({
    queryKey: computed(() => ['admin', 'badges', 'by-slug', slugValue.value]),
    enabled: computed(() => Boolean(slugValue.value)),
    queryFn: async (): Promise<PartnerBadge | null> => {
      const response = await api.admin.badges.listBadges({
        query: { search: slugValue.value, limit: 5, status: 'active' },
      });
      return (response.data || []).find((badge: PartnerBadge) => badge.slug === slugValue.value) ?? null;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const badge = computed(() => data.value ?? null);
  const badgeName = computed(() => badge.value?.name || '');

  return { badge, badgeName, isLoading };
}

export default useBadgeBySlug;
